import { create } from 'zustand';

function hexToRgbTriplet(hex) {
  if (!hex) return null;
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  const bigint = parseInt(full, 16);
  if (Number.isNaN(bigint)) return null;
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r} ${g} ${b}`;
}

function computeDarker(hex, factor = 0.8) {
  const value = hex?.replace('#', '');
  if (!value) return null;
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  const bigint = parseInt(full, 16);
  if (Number.isNaN(bigint)) return null;
  const r = Math.max(0, Math.floor(((bigint >> 16) & 255) * factor));
  const g = Math.max(0, Math.floor(((bigint >> 8) & 255) * factor));
  const b = Math.max(0, Math.floor((bigint & 255) * factor));
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

// Faradai is the only whitelabel without dedicated build infra — it shares the
// main CG frontend build and is activated via hostname match at runtime.
const FARADAI_SVG =
  '<svg width="110" height="31" viewBox="0 0 110 31" fill="none" xmlns="http://www.w3.org/2000/svg">\n<path d="M41.2675 12.5465V13.8769L39.6082 13.976V16.1385H41.2675V23.7423H44.8375V16.1385H47.297V13.8769H44.8375V13.0421C44.8375 12.1892 45.094 11.8918 45.9301 11.8918H47.6869L48.0177 9.64847C46.9068 9.4851 45.7856 9.40489 44.6631 9.40849C42.1446 9.40849 41.2675 10.6997 41.2675 12.5465Z" fill="#A5A5A5"/>\n<path d="M64.4443 14.889L64.1314 13.8769H61.3667V23.7423H64.906V16.8375C65.7779 16.5898 66.676 16.4496 67.5809 16.4202H68.517L68.8299 13.8664H68.2913C67.2449 13.869 65.9523 13.843 64.4443 14.889Z" fill="#A5A5A5"/>\n<path d="M109.521 13.8769H105.989V23.7423H109.521V13.8769Z" fill="#4129A8"/>\n<path d="M108.697 9.70064H106.83C106.148 9.70064 105.958 10.058 105.958 10.7727V11.8161C105.958 12.5309 106.148 12.8596 106.83 12.8596H108.697C109.382 12.8596 109.549 12.5231 109.549 11.8161V10.778C109.549 10.058 109.382 9.70064 108.697 9.70064Z" fill="#4129A8"/>\n<path d="M54.7679 13.6265C52.9885 13.6553 51.2138 13.8209 49.459 14.1221L49.7719 16.4697C51.3928 16.3706 52.9521 16.3315 53.9292 16.3315C54.9551 16.3315 55.586 16.6289 55.604 17.4636V17.701H52.0545C50.1233 17.701 48.9512 18.6531 48.9512 20.2208V21.1468C48.9512 22.7119 50.1233 23.7553 52.0545 23.7553H54.16L55.9091 23.4684L55.9963 23.7762H58.8175V17.4766C58.8021 14.5394 57.3838 13.6265 54.7679 13.6265ZM55.604 21.4729L53.2445 21.6189C52.6597 21.6007 52.4263 21.3033 52.4263 20.8364V20.5208C52.4263 20.0643 52.6597 19.7669 53.2445 19.7382H55.604V21.4729Z" fill="#A5A5A5"/>\n<path d="M88.6803 14.0282L85.1898 14.0151C83.2586 14.0151 82.0866 14.9698 82.0866 16.5376V21.152C82.0866 22.7171 83.2586 23.7423 85.1898 23.7423H87.498L89.2497 23.4058L89.4395 23.7631H91.9733V9.8702H88.6803V14.0282ZM85.5617 20.7451V17.1558C85.5617 16.6993 85.7951 16.4019 86.3798 16.3732L88.688 16.3576V21.4311L86.3798 21.5198C85.7951 21.5016 85.5617 21.212 85.5617 20.7451Z" fill="#A5A5A5"/>\n<path d="M75.8699 13.6265C74.0913 13.6553 72.3175 13.8209 70.5636 14.1221L70.8739 16.4697C72.4948 16.3706 74.0566 16.3315 75.0312 16.3315C76.0571 16.3315 76.688 16.6289 76.7059 17.4636V17.701H73.1642C71.2304 17.701 70.0609 18.6531 70.0609 20.2208V21.1468C70.0609 22.7119 71.2304 23.7553 73.1642 23.7553H75.2697L77.0188 23.4684L77.106 23.7762H79.9271V17.4766C79.9118 14.5394 78.4858 13.6265 75.8699 13.6265ZM76.7059 21.4729L74.349 21.6189C73.7643 21.6007 73.5283 21.3033 73.5283 20.8364V20.5208C73.5283 20.0643 73.7643 19.7669 74.349 19.7382H76.7059V21.4729Z" fill="#A5A5A5"/>\n<path d="M99.7417 13.6265C97.9623 13.6552 96.1876 13.8208 94.4328 14.1221L94.7457 16.4697C96.364 16.3706 97.9259 16.3315 98.903 16.3315C99.8802 16.3315 100.557 16.6289 100.578 17.4636V17.701H97.0283C95.0971 17.701 93.925 18.6531 93.925 20.2208V21.1468C93.925 22.7119 95.0971 23.7553 97.0283 23.7553H99.1339L100.883 23.4684L100.97 23.7762H103.791V17.4766C103.781 14.5394 102.358 13.6265 99.7417 13.6265ZM100.578 21.4729L98.2183 21.6189C97.6335 21.6007 97.4002 21.3033 97.4002 20.8364V20.5208C97.4002 20.0643 97.6335 19.7669 98.2183 19.7382H100.578V21.4729Z" fill="#4129A8"/>\n<path d="M28.7392 12.7917H12.8793L6.35742 19.4252C5.90713 19.8832 5.29641 20.1405 4.65962 20.1405C4.02282 20.1405 3.4121 19.8832 2.96181 19.4252C2.51153 18.9672 2.25856 18.346 2.25856 17.6983C2.25856 17.0507 2.51153 16.4295 2.96181 15.9715L10.189 8.62072C10.3513 8.45556 10.5362 8.315 10.7379 8.20335C10.7917 8.17466 10.8481 8.14597 10.9148 8.11727C10.9283 8.10827 10.943 8.10124 10.9584 8.09641C11.0046 8.07554 11.0533 8.05989 11.0995 8.04163L11.1713 8.01554L11.2662 7.98946H11.3021L11.4072 7.96337L11.4867 7.95033H11.5252C11.5624 7.94238 11.6001 7.93715 11.638 7.93468H27.9031C26.9062 6.05255 25.5276 4.4073 23.8584 3.10788C22.1893 1.80846 20.2679 0.884525 18.2213 0.397255C16.1747 -0.0900137 14.0496 -0.129497 11.9869 0.28142C9.92417 0.692336 7.9708 1.54428 6.25615 2.78082C4.54149 4.01737 3.10469 5.61031 2.04087 7.45415C0.977059 9.29799 0.310511 11.3507 0.0853707 13.4762C-0.13977 15.6018 0.0816336 17.7518 0.73492 19.7838C1.38821 21.8158 2.45847 23.6834 3.87483 25.263L13.2615 15.7159C13.4221 15.5516 13.6052 15.4119 13.8052 15.3011H13.8206C13.8642 15.2776 13.9206 15.2489 13.9873 15.2203C14.0013 15.2123 14.0159 15.2053 14.0309 15.1994C14.077 15.1785 14.1258 15.1629 14.1719 15.1446H14.1771L14.2489 15.1185L14.3438 15.0924H14.3797L14.4848 15.0664H14.6028C14.64 15.0584 14.6777 15.0532 14.7156 15.0507H18.9012C19.5246 15.0714 20.1156 15.3379 20.5494 15.7937C20.9832 16.2496 21.2257 16.8592 21.2257 17.4936C21.2257 18.128 20.9832 18.7375 20.5494 19.1934C20.1156 19.6493 19.5246 19.9157 18.9012 19.9364H15.9518L7.64488 28.3359C9.90304 29.6241 12.4533 30.2891 15.0423 30.2648C17.6313 30.2406 20.169 29.5278 22.4035 28.1974C24.6379 26.867 26.4913 24.9652 27.7795 22.6809C29.0678 20.3966 29.7461 17.8093 29.7471 15.1759C29.7471 14.3298 29.6776 13.4852 29.5393 12.6509C29.2826 12.7442 29.0119 12.7919 28.7392 12.7917ZM24.9871 19.8869C24.5117 19.8869 24.047 19.7435 23.6517 19.4748C23.2565 19.2061 22.9485 18.8242 22.7667 18.3775C22.5849 17.9307 22.5374 17.4391 22.6304 16.9649C22.7233 16.4907 22.9524 16.0552 23.2888 15.7135C23.6251 15.3718 24.0535 15.1392 24.5198 15.0452C24.9862 14.9512 25.4694 14.9999 25.9085 15.1853C26.3475 15.3707 26.7227 15.6844 26.9864 16.0867C27.2502 16.489 27.3907 16.9618 27.3902 17.4453C27.3895 18.0931 27.136 18.7141 26.6854 19.1719C26.2348 19.6297 25.624 19.8869 24.9871 19.8869Z" fill="#4129A8"/>\n</svg>';

const FARADAI_BRAND = {
  key: 'faradai',
  name: 'Faradai',
  primaryColor: '#4129A8',
  primaryColorDark: '#2d1c75',
  secondaryColor: '#A5A5A5',
  secondaryColorLight: '#C7C7C7',
  secondaryColorLightest: '#F4F6F8',
  logoUrl: `data:image/svg+xml;utf8,${encodeURIComponent(FARADAI_SVG)}`,
  faviconUrl: 'https://faradai.ai/favicon.ico',
  supportEmail: 'support@faradai.ai',
  helpUrl: 'https://help.faradai.ai',
  websiteUrl: 'https://faradai.ai',
  tosUrl: 'https://faradai.ai/terms',
};

function isFaradaiHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'faradai.ai' || host.endsWith('.faradai.ai');
}

// Restricts logo URLs to safe schemes so partners can't inject XSS via the
// brand=embed flow. https only, plus inline SVG data URIs (which are common
// for vector logos and considered safe when the document CSP allows them).
function sanitizeLogoUrl(value) {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.toLowerCase() === 'none') return null;
  if (/^https:\/\//i.test(trimmed)) return trimmed;
  if (/^data:image\/svg\+xml[,;]/i.test(trimmed)) return trimmed;
  return null;
}

function readParam(params, key) {
  const raw = params.get(key);
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

// Reads brand overrides from URL query params. Used by the partner iframe
// embed (/embed/*) so each partner can theme the embed without a dedicated
// whitelabel build.
function getBrandFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (params.toString() === '') return null;

  const primaryColor = readParam(params, 'primaryColor');
  const secondaryColor = readParam(params, 'secondaryColor');
  const primaryColorDark = readParam(params, 'primaryColorDark');
  const name = readParam(params, 'name');
  const logoUrl = sanitizeLogoUrl(readParam(params, 'logoUrl'));
  const faviconUrl = readParam(params, 'faviconUrl');

  // If none of the brand keys appear, return null so the next-priority source
  // (Faradai hostname / env vars) wins.
  if (!primaryColor && !secondaryColor && !primaryColorDark && !name && !logoUrl && !faviconUrl) {
    return null;
  }

  return {
    key: 'partner-embed',
    name: name || '',
    primaryColor: primaryColor || undefined,
    primaryColorDark:
      primaryColorDark || (primaryColor ? computeDarker(primaryColor, 0.7) : undefined),
    secondaryColor: secondaryColor || undefined,
    secondaryColorLight: undefined,
    secondaryColorLightest: undefined,
    logoUrl: logoUrl || undefined,
    faviconUrl: faviconUrl || undefined,
  };
}

// Optional tenant typeface: a Google Fonts family name. Loads only the weights
// the app's Tailwind classes use, and feeds --brand-font (the head of the body
// font stack in styles/main.css). Unset on a brand = the system stack.
function applyBrandFont(root, family) {
  const id = 'brand-font-stylesheet';
  if (!family) {
    root.style.removeProperty('--brand-font');
    document.getElementById(id)?.remove();
    return;
  }
  let link = document.getElementById(id);
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700&display=swap`;
  root.style.setProperty('--brand-font', `'${family}'`);
}

function applyBranding(brand) {
  // Apply CSS variables
  const root = document.documentElement;
  applyBrandFont(root, brand.fontFamily);
  const set = (name, hex) => {
    const triplet = hexToRgbTriplet(hex);
    if (triplet) root.style.setProperty(name, triplet);
  };
  set('--primary-color', brand.primaryColor);
  set('--primary-color-dark', brand.primaryColorDark);
  set('--secondary-color', brand.secondaryColor);
  set('--secondary-color-light', brand.secondaryColorLight);
  set('--secondary-color-lightest', brand.secondaryColorLightest);

  // Faradai runs on the shared CG build, so the HTML is baked with CG title /
  // favicon. Sync them from JS for faradai hosts. No-op for other brands since
  // their dedicated builds already have matching values in index.html.
  if (brand.name) document.title = brand.name;
  if (brand.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = brand.faviconUrl;
  }
}

function getBrandFromEnv() {
  if (isFaradaiHost()) return FARADAI_BRAND;

  const primaryColor = process.env.REACT_APP_BRAND_PRIMARY_COLOR || '#00B894';

  return {
    key: process.env.REACT_APP_WHITELABEL || 'carbonglance',
    name: process.env.REACT_APP_BRAND_NAME || 'Carbon Glance',
    primaryColor,
    primaryColorDark:
      process.env.REACT_APP_BRAND_PRIMARY_COLOR_DARK || computeDarker(primaryColor, 0.7),
    secondaryColor: process.env.REACT_APP_BRAND_SECONDARY_COLOR || '#607489',
    secondaryColorLight: process.env.REACT_APP_BRAND_SECONDARY_COLOR_LIGHT || '#9FABB8',
    secondaryColorLightest: process.env.REACT_APP_BRAND_SECONDARY_COLOR_LIGHTEST || '#F4F6F8',
    logoUrl: process.env.REACT_APP_BRAND_LOGO_URL || undefined,
    faviconUrl: process.env.REACT_APP_BRAND_FAVICON_URL || '/favicon.ico',
    fontFamily: process.env.REACT_APP_BRAND_FONT_FAMILY || undefined,
    supportEmail: process.env.REACT_APP_BRAND_SUPPORT_EMAIL || 'support@carbonglance.com',
    helpUrl: process.env.REACT_APP_BRAND_HELP_URL || 'https://help.carbonglance.com',
    websiteUrl: process.env.REACT_APP_BRAND_WEBSITE_URL || 'https://carbonglance.com',
    tosUrl:
      process.env.REACT_APP_BRAND_TOS_URL ||
      'https://carbonglance.com/carbon-glance-software-terms-of-service-for-operators/',
  };
}

// Precedence: URL query params > Faradai hostname > env vars > defaults.
// URL overrides are merged on top of the env brand so partners can override
// just primaryColor + logo without having to specify every secondary token.
function getInitialBrand() {
  const base = getBrandFromEnv();
  const fromUrl = getBrandFromUrl();
  if (!fromUrl) return base;
  const merged = { ...base };
  for (const key of Object.keys(fromUrl)) {
    if (fromUrl[key] !== undefined && fromUrl[key] !== null && fromUrl[key] !== '') {
      merged[key] = fromUrl[key];
    }
  }
  return merged;
}

const initialBrand = getInitialBrand();

// 'carbonglance' is the default brand key (getBrandFromEnv above); anything
// else — a Faradai host, a REACT_APP_WHITELABEL build, or a partner-embed URL
// override — is a whitelabel tenant. Centralised because the check gates
// CG-only commerce (pricing, Stripe checkout, the CG demo call) in several
// places and a stray `!==` typo would silently expose CG's rates.
export const isWhitelabelBrand = (brand) => !!brand?.key && brand.key !== 'carbonglance';

export const useBranding = create((set, get) => ({
  ...initialBrand,
  setBrand: (brand) => {
    set(brand);
    applyBranding(brand);
  },
  applyCss: () => applyBranding(get()),
}));

// Ensure branding (CSS variables + document title) reflects the initial brand at load
if (typeof window !== 'undefined') {
  applyBranding(initialBrand);
}
